import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Pagination from '@cloudscape-design/components/pagination';
import Select from '@cloudscape-design/components/select';
import Table from '@cloudscape-design/components/table';
import { useNavigate } from 'react-router-dom';
import { apiJson } from '../utils/api';

interface StorySummary {
  type_key: string;
  make?: string;
  model?: string;
  category?: string;
  story_status?: string;
  story_generated_at?: string;
  story_highlights?: string[];
}

const STATUS_OPTIONS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Published', value: 'approved' },
  { label: 'Pending', value: 'pending' },
  { label: 'Unpublished', value: 'rejected' },
];

const PAGE_SIZE = 25;

const StoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<StorySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('all');
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextKey, setNextKey] = useState<string | undefined>(undefined);

  const fetchPage = useCallback(
    async (idx: number, cursor: string | undefined): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({ pageSize: String(PAGE_SIZE) });
        if (cursor) qs.set('nextPageKey', cursor);
        if (status !== 'all') qs.set('status', status);
        const data = await apiJson<{ items: StorySummary[]; nextPageKey?: string }>(
          `/admin/stories?${qs.toString()}`,
        );
        setItems(data.items);
        setNextKey(data.nextPageKey);
        setPageIndex(idx);
        if (data.nextPageKey) {
          setPageCursors((prev) => {
            const c = [...prev];
            c[idx + 1] = data.nextPageKey;
            return c;
          });
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [status],
  );

  useEffect(() => {
    setPageCursors([undefined]);
    void fetchPage(0, undefined);
  }, [status, fetchPage]);

  return (
    <ContentLayout header={<Header variant="h1">Aircraft Stories</Header>}>
      {error && <Alert type="error">{error}</Alert>}
      <Table
        loading={loading}
        loadingText="Loading stories"
        items={items}
        variant="full-page"
        onRowClick={({ detail }) =>
          navigate(`/stories/${encodeURIComponent(detail.item.type_key)}`)
        }
        columnDefinitions={[
          {
            id: 'aircraft',
            header: 'Aircraft',
            cell: (s) => (
              <Link
                onFollow={(e) => {
                  e.preventDefault();
                  navigate(`/stories/${encodeURIComponent(s.type_key)}`);
                }}
                href={`/stories/${encodeURIComponent(s.type_key)}`}
              >
                {[s.make, s.model].filter(Boolean).join(' ') || s.type_key}
              </Link>
            ),
            isRowHeader: true,
          },
          { id: 'category', header: 'Category', cell: (s) => s.category ?? '—' },
          {
            id: 'status',
            header: 'Status',
            cell: (s) => (
              <Badge color={(s.story_status ?? 'approved') === 'approved' ? 'green' : 'grey'}>
                {s.story_status ?? 'approved'}
              </Badge>
            ),
          },
          {
            id: 'highlight',
            header: 'First highlight',
            cell: (s) => s.story_highlights?.[0] ?? '—',
          },
          {
            id: 'generated',
            header: 'Generated',
            cell: (s) => s.story_generated_at?.slice(0, 10) ?? '—',
          },
        ]}
        filter={
          <Select
            selectedOption={STATUS_OPTIONS.find((o) => o.value === status) ?? STATUS_OPTIONS[0]}
            onChange={(e) => setStatus(e.detail.selectedOption.value ?? 'all')}
            options={STATUS_OPTIONS}
          />
        }
        pagination={
          <Pagination
            currentPageIndex={pageIndex + 1}
            pagesCount={pageCursors.length}
            openEnd={Boolean(nextKey)}
            onChange={({ detail }) => {
              const idx = detail.currentPageIndex - 1;
              void fetchPage(idx, pageCursors[idx]);
            }}
          />
        }
        empty={
          <Box textAlign="center" color="text-body-secondary">
            No stories{status !== 'all' ? ' with this status' : ''} yet. They generate the first
            time an aircraft type is identified.
          </Box>
        }
        header={
          <Header counter={nextKey ? `(${items.length}+)` : `(${items.length})`}>
            Generated stories
          </Header>
        }
      />
    </ContentLayout>
  );
};

export default StoriesPage;
